import { CheckCircleIcon, CreditCardIcon, DocumentTextIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import { PaymentStatus } from '../../@types/PaymentStatus';
import type { OfferDetails, SubscriptionDetails } from '../../services/paymentApi';
import { paymentApi  } from '../../services/paymentApi';
import type { RootState } from '../../store/store';
import { getSubscriptionStatusMessage, hasActiveSubscription, isSubscriptionExpiringSoon } from '../../utils/subscriptions';
import Badge from '../common/Badge';

interface SubscriptionPanelProps {
  onClose?: () => void;
}

const SubscriptionPanel: React.FC<SubscriptionPanelProps> = () => {
  const { company, token } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [offerDetails, setOfferDetails] = useState<OfferDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSubscriptionData = async() => {
    if (!token || !company) {return;}
    try {
      setLoading(true);
      setError(null);
      const data = await paymentApi.getSubscription(company.id, token);
      setSubscriptionDetails(data);
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Impossible de charger les informations d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const loadOfferDetails = async() => {
    if (!token || !company) {return;}
    try {
      const offer = await paymentApi.getOffer(company.id, token);
      setOfferDetails(offer);
    } catch (err) {
      console.error('Error loading offer:', err);
    }
  };

  useEffect(() => {
    if (company && token) {
      loadSubscriptionData();
      loadOfferDetails();
    }
  }, [company, token]);

  const handleSubscribe = () => {
    // Navigate to checkout page
    window.location.href = '/payment/checkout';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {return 'N/A';}
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (cents: number) => {
    return `${(cents / 100).toFixed(2)} €`;
  };

  const getStatusIcon = () => {
    if (!company) {return null;}
    
    const isActive = hasActiveSubscription(company);
    const expiringSoon = isSubscriptionExpiringSoon(company);

    if (isActive && !expiringSoon) {
      return <CheckCircleIcon className="h-8 w-8 text-green-500" />;
    }
    if (expiringSoon) {
      return <ExclamationTriangleIcon className="h-8 w-8 text-orange-500" />;
    }
    return <XCircleIcon className="h-8 w-8 text-red-500" />;
  };

  const getStatusBadge = () => {
    if (!company) {return null;}

    const isActive = hasActiveSubscription(company);
    const expiringSoon = isSubscriptionExpiringSoon(company);

    if (isActive && !expiringSoon) {
      return <Badge mode="success" text="Actif" />;
    }
    if (expiringSoon) {
      return <Badge mode="warning" text="Expire bientôt" />;
    }
    
    switch (company.paymentStatus) {
      case PaymentStatus.TRIAL:
        return <Badge mode="warning" text="Période d'essai" />;
      case PaymentStatus.SUSPENDED:
        return <Badge mode="warning" text="Suspendu" />;
      case PaymentStatus.CANCELLED:
        return <Badge mode="error" text="Annulé" />;
      default:
        return <Badge mode="error" text="Inactif" />;
    }
  };

  const getSubscriptions = () => {
    const s = subscriptionDetails?.subscriptions;
    return Array.isArray(s) ? s : s?.Results ?? [];
  };

  const renderSubscriptionList = () => {
    const subs = getSubscriptions();
    if (subs.length === 0) {
      return (
        <div className="py-8 text-center text-gray-500">
          Aucun abonnement trouvé
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {subs.map((sub: { TitleLocalized?: string; ReferenceOffer?: string; StateSubscription?: string; DateStart?: string | null; DateNextBilling?: string | null; AmountRecurrence?: number }, index: number) => (
          <div key={index} className="rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold text-gray-900">
                {sub.TitleLocalized || sub.ReferenceOffer}
              </div>
              <Badge 
                mode={sub.StateSubscription === 'Running' ? 'success' : 'warning'} 
                text={sub.StateSubscription} 
              />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-600">
                Début: <span className="font-medium text-gray-900">{formatDate(sub.DateStart)}</span>
              </div>
              <div className="text-gray-600">
                Prochaine facturation: <span className="font-medium text-gray-900">{formatDate(sub.DateNextBilling)}</span>
              </div>
              <div className="col-span-2 text-gray-600">
                Montant: <span className="font-medium text-gray-900">{formatPrice(sub.AmountRecurrence)}</span> / mois
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getInvoices = () => {
    const inv = subscriptionDetails?.invoices;
    if (Array.isArray(inv)) {return inv;}
    if (inv && 'Results' in inv && Array.isArray(inv.Results)) {return inv.Results;}
    if (inv && 'Items' in inv && Array.isArray(inv.Items)) {return inv.Items;}
    return [];
  };

  const renderInvoiceList = () => {
    const invoices = getInvoices();
    if (invoices.length === 0) {
      return (
        <div className="py-8 text-center text-gray-500">
          Aucune facture disponible
        </div>
      );
    }

    return (
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {invoices.map((invoice: { ReferenceInvoice?: string; DateInvoice?: string | null; TotalAmountTTC?: number; StateInvoice?: string; FullNumber?: string; DateIssue?: string | null; AmountTotal?: number; AmountTotalTTC?: number }, index: number) => (
          <div key={index} className="rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">
                    Facture #{invoice.ReferenceInvoice}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(invoice.DateInvoice)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900">
                  {formatPrice(invoice.TotalAmountTTC)}
                </div>
                <Badge 
                  mode={invoice.StateInvoice === 'Paid' ? 'success' : 'warning'} 
                  text={invoice.StateInvoice === 'Paid' ? 'Payée' : invoice.StateInvoice} 
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={100} />
        <Skeleton height={200} />
        <Skeleton height={150} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Badge mode="error" text={error} />
      </div>
    );
  }

  if (subscriptionDetails?.portalLink) {
    return (
      <div className="h-full min-h-[600px] w-full">
        <iframe
          src={subscriptionDetails.portalLink}
          className="h-full w-full rounded-lg border-0"
          title="Gestion de l'abonnement"
        />
      </div>
    );
  }

  const isActive = company && hasActiveSubscription(company);

  return (
    <div className="space-y-6 p-6">
      {/* Status Overview */}
      <div className="rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 p-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            {getStatusIcon()}
          </div>
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-900">Statut de l'abonnement</h3>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-gray-700">
              {company && getSubscriptionStatusMessage(company)}
            </p>
            {company?.proAbonoSubscriptionEnd && (
              <p className="mt-1 text-xs text-gray-600">
                Expire le {formatDate(company.proAbonoSubscriptionEnd)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Offer Details */}
      {offerDetails && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <CreditCardIcon className="h-6 w-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">Détails de l'offre</h3>
          </div>
          <div className="mb-4 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="text-center">
              <div className="mb-2 text-4xl font-bold">
                {offerDetails.AmountRecurrence ? formatPrice(offerDetails.AmountRecurrence) : '249.00 €'}
              </div>
              <div className="text-blue-100">HT par mois</div>
              <div className="mt-1 text-sm text-blue-100">Facturation mensuelle • Engagement 12 mois</div>
            </div>
          </div>
          
          {/* Launch Promotion Banner */}
          <div className="mb-4 rounded-lg border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="mb-1 font-bold text-green-900"><span role="img" aria-label="party popper">🎉</span> Offre de lancement module Croissance</h4>
                <p className="text-sm text-green-800">
                  Frais de mise en service de <span className="font-bold line-through">990€ HT</span> <span className="font-bold text-green-600">OFFERTS</span> pour la saison de lancement !
                </p>
              </div>
            </div>
          </div>
          <div className="mb-3 text-sm font-semibold text-gray-900">Ce qui est inclus :</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
              <span>Accès à tous les modules</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
              <span>Communication, Prospection et Monitoring</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
              <span>Support client prioritaire</span>
            </li>
          </ul>
        </div>
      )}

      {/* Subscribe Button */}
      {!isActive && (
        <div className="text-center">
          <button
            onClick={handleSubscribe}
            className="rounded-lg bg-blue-600 px-8 py-3 font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg"
          >
            <CreditCardIcon className="mr-2 inline h-5 w-5" />
            S'abonner maintenant
          </button>
        </div>
      )}

      {/* Active Subscriptions */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <DocumentTextIcon className="h-6 w-6 text-gray-600" />
          Mes abonnements
        </h3>
        {renderSubscriptionList()}
      </div>

      {/* Invoices */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <DocumentTextIcon className="h-6 w-6 text-gray-600" />
          Mes factures
        </h3>
        {renderInvoiceList()}
      </div>
    </div>
  );
};

export default SubscriptionPanel;

